import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { KeycloakClientRole } from '../../../auth/clientRoles';
import { authQueryKeys } from '../../../auth/authQueryKeys';
import { userService } from '../../../services/UserService';
import type {
  CreateDirectoryUserInput,
  SetDirectoryUserEnabledInput,
  UpdateDirectoryUserInput,
} from '../../../types/user';
import { tenantQueryKeys } from '../tenantQueryKeys';

export type SaveDirectoryUserInput = UpdateDirectoryUserInput;

function invalidateTenantUserPhotoQueries(
  queryClient: ReturnType<typeof useQueryClient>,
  userId: string,
) {
  void queryClient.invalidateQueries({
    queryKey: tenantQueryKeys.users(),
  });
  void queryClient.invalidateQueries({
    queryKey: tenantQueryKeys.user(userId),
  });
  void queryClient.invalidateQueries({
    queryKey: authQueryKeys.currentUserProfile(),
  });
}

export function useCreateTenantUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateDirectoryUserInput) =>
      userService.createUser(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.users(),
      });
    },
  });
}

export function useUpdateTenantUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SaveDirectoryUserInput) =>
      userService.updateUser(input),
    onSuccess: (_result, input) => {
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.users(),
      });
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.user(input.userId),
      });
    },
  });
}

export function useUploadTenantUserPhotoMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId, file }: { userId: string; file: File }) =>
      userService.uploadUserPhoto(userId, file),
    onSuccess: (_result, input) => {
      invalidateTenantUserPhotoQueries(queryClient, input.userId);
    },
  });
}

export function useDeleteTenantUserPhotoMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ userId }: { userId: string }) =>
      userService.deleteUserPhoto(userId),
    onSuccess: (_result, input) => {
      invalidateTenantUserPhotoQueries(queryClient, input.userId);
    },
  });
}

export function useSetTenantUserRolesMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      userId,
      roleNames,
    }: {
      userId: string;
      roleNames: readonly KeycloakClientRole[];
    }) => userService.setUserRoles(userId, roleNames),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.user(variables.userId),
      });
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.userRoles(variables.userId),
      });
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.users(),
      });
    },
  });
}

export function useSetTenantUserEnabledMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: SetDirectoryUserEnabledInput) =>
      userService.setUserEnabled(input),
    onSuccess: (_result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.users(),
      });
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.user(variables.userId),
      });
    },
  });
}

export function useDeleteTenantUserMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => userService.deleteUser(userId),
    onSuccess: (_result, userId) => {
      void queryClient.invalidateQueries({
        queryKey: tenantQueryKeys.users(),
      });
      void queryClient.removeQueries({
        queryKey: tenantQueryKeys.user(userId),
      });
      void queryClient.removeQueries({
        queryKey: tenantQueryKeys.userRoles(userId),
      });
    },
  });
}

export function useResetTenantUserPasswordMutation() {
  return useMutation({
    mutationFn: ({
      userId,
      password,
      temporary,
    }: {
      userId: string;
      password: string;
      temporary: boolean;
    }) => userService.resetUserPassword(userId, password, temporary),
  });
}
